import { Link } from "react-router-dom";

/**
 * IconButton — square, icon-only sticker button (border + hard shadow + bouncy press).
 * Reusable replacement for the ad-hoc h-8/h-9/h-10 icon buttons across the app.
 *
 * size: "sm" (h-9 w-9, 36px — dense in-grid/in-chip rows only) | "md" (h-10 w-10, 40px — default)
 * tone: "paper" | "candy" | "pink" | "mint" | "ink"
 * Requires an aria-label (also applied as title) for accessibility.
 */
const SIZES = { sm: "h-9 w-9", md: "h-10 w-10" };
const ICON_PX = { sm: 16, md: 18 };
const TONES = {
  paper: "bg-white text-ink border-ink",
  candy: "bg-candy text-white border-ink is-candy",
  pink: "bg-pink text-ink border-ink is-pink",
  mint: "bg-mint text-ink border-ink",
  ink: "bg-ink text-white border-ink",
};

export const IconButton = ({
  icon: Icon,
  size = "md",
  tone = "paper",
  "aria-label": ariaLabel,
  as,
  to,
  href,
  onClick,
  type = "button",
  disabled = false,
  strokeWidth = 2.5,
  className = "",
  "data-testid": testId,
  ...rest
}) => {
  const cls = `inline-flex items-center justify-center shrink-0 rounded-full border-2 shadow-sticker-xs candy-press ${
    SIZES[size]
  } ${TONES[tone]} ${
    disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : "cursor-pointer"
  } ${className}`;
  const inner = Icon ? <Icon size={ICON_PX[size]} strokeWidth={strokeWidth} /> : null;

  if (to) {
    return (
      <Link
        to={to}
        aria-label={ariaLabel}
        title={ariaLabel}
        className={cls}
        data-testid={testId}
        {...rest}
      >
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        aria-label={ariaLabel}
        title={ariaLabel}
        className={cls}
        data-testid={testId}
        {...rest}
      >
        {inner}
      </a>
    );
  }
  const Tag = as || "button";
  return (
    <Tag
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={cls}
      data-testid={testId}
      {...rest}
    >
      {inner}
    </Tag>
  );
};

export default IconButton;
